import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import {
  selectActionList,
  selectCurrentVariantCohort,
  selectSubscriptionList,
} from 'root-redux/selects/common'
import {
  GET_SUBSCRIPTION_LIST,
  getSubscriptionListAction,
  setScreenNameAction,
  startFetching,
} from 'root-redux/actions/common'
import { CHECK_PAYMENT_REQUEST_BUTTON } from 'modules/purchase/redux/actions'
import { selectSubscription } from 'modules/purchase/redux/selects'
import { selectUUID } from 'root-redux/selects/user'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { eventLogger } from 'services/eventLogger.service'
import { createProductId } from 'helpers/createProductId'
import {
  Color,
  PagePath,
  ScreenName,
  SubscriptionListType,
} from 'root-constants'
import { ISubscription } from 'models/subscriptions.model'
import { Spinner } from 'components/Spinner'
import { SvgImage } from 'components/SvgImage'
import { TAppDispatch } from 'root-redux/store/store'
import { Checkout } from 'modules/purchase/pages/Checkout'
import { Container } from 'components/Container'
import {
  PERSONAL_PLAN_INCLUDES_ITEMS,
  PRICE_DESCRIPTION_KEYS,
  USER_BENEFITS,
} from 'modules/purchase/pages/PurchaseVariant2/constants'
import featuredImagePng from 'assets/images/featured-light.png'
import { MoneyBackGuarantee } from 'modules/purchase/components/MoneyBackGuarantee'
import { SubscriptionsBlockV2 } from 'modules/purchase/components/SubscriptionsBlockV2'
import checkIcon from 'assets/images/sprite/check-icon.svg'
import { CustomerReviewsListV2 } from 'modules/purchase/components/CustomerReviewsListV2'
import { CURRENCY_SYMBOLS } from 'modules/purchase/constants'
import { TermsOfUseLink } from 'components/TermsOfUseLink'
import { StyledPurchaseVariant2 as S } from './PurchaseVariant2.styles'
import { SubheaderWithTimer } from '../../components/SubheaderWithTimer'

export const PurchaseVariant2: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const buttonElementRef = useRef<HTMLDivElement>(null)
  const [isCheckoutShown, setIsCheckoutShown] = useState(false)

  const uuid = useSelector(selectUUID)
  const cohort = useSelector(selectCurrentVariantCohort)
  const subscriptions = useSelector(selectSubscriptionList)
  const selectedSubscription = useSelector(selectSubscription)
  const fetchingActionsList = useSelector(selectActionList)

  const arePricesReady = useMemo(
    () => !fetchingActionsList?.includes(GET_SUBSCRIPTION_LIST),
    [fetchingActionsList],
  )

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--header-background-color',
      `${isCheckoutShown ? 'transparent' : Color.GRADIENT}`,
    )
  }, [isCheckoutShown])

  useEffect(() => {
    dispatch(setScreenNameAction(ScreenName.ONBOARDING))
  }, [dispatch])

  useEffect(() => {
    if (!cohort) return

    dispatch(
      getSubscriptionListAction({
        cohort,
        type: SubscriptionListType.PURCHASE,
      }),
    )
  }, [dispatch, cohort])

  useEffect(() => {
    if (!subscriptions.length) return

    eventLogger.logSalePageShown({
      productIds: subscriptions.map(
        ({ periodName, periodQuantity, currentPrice }) =>
          createProductId({
            periodName,
            periodQuantity,
            price: currentPrice,
          }),
      ),
      screenName: ScreenName.ONBOARDING,
    })
  }, [subscriptions])

  const handleShowPayment = useCallback(() => {
    dispatch(startFetching(CHECK_PAYMENT_REQUEST_BUTTON))
    setIsCheckoutShown(true)

    window.fbq('track', 'AddToCart', {}, { eventID: uuid })
    googleAnalyticsLogger.logPageView(`${PagePath.CHECKOUT}/${cohort}`)
    googleAnalyticsLogger.logAddingToCart(selectedSubscription as ISubscription)
  }, [dispatch, selectedSubscription, uuid, cohort])

  return !arePricesReady ? (
    <S.SpinnerBackground>
      <Spinner />
    </S.SpinnerBackground>
  ) : (
    <>
      {!isCheckoutShown && (
        <S.Wrapper>
          <S.ProgramPlanContainer>
            <S.Hero>
              <video
                id="background-video"
                width="100%"
                autoPlay
                loop
                playsInline
                muted
                poster="https://cdn-prod-unified-content.gismart.xyz/ee0e4f309c9a47317d3344b5bcd25dcbf0727a91833d5c6e21b842b53c406878/moodbalance-preview.png"
                src="https://cdn-prod-unified-content.gismart.xyz/ee0e4f309c9a47317d3344b5bcd25dcbf0727a91833d5c6e21b842b53c406878/MoodBalance_Paywall.mp4"
              />
            </S.Hero>
            <Container>
              <S.Title>
                <Trans
                  i18nKey="purchase2.title"
                  components={{ highlighted: <span /> }}
                />
              </S.Title>
              <S.Description>{t('purchase2.description')}</S.Description>
              <S.Received>
                <S.ListTitle>{t('purchase2.listTitle')}</S.ListTitle>
                <ul>
                  {PERSONAL_PLAN_INCLUDES_ITEMS.map(({ id, value, icon }) => (
                    <S.ListItem key={id}>
                      <SvgImage svg={icon} width={30} />
                      {t(value as any)}
                    </S.ListItem>
                  ))}
                </ul>
              </S.Received>
            </Container>
          </S.ProgramPlanContainer>
          <SubheaderWithTimer
            elemForComparisonRef={buttonElementRef}
            onButtonClick={handleShowPayment}
          />
          <S.Container>
            <SubscriptionsBlockV2
              onButtonClick={handleShowPayment}
              elemForComparisonRef={buttonElementRef}
            />
            <S.FeaturedBlockContainer>
              <S.FeaturedBlockTitle>{t`purchase.featuredBlockTitle`}</S.FeaturedBlockTitle>
              <picture>
                <img src={featuredImagePng} alt="body" />
              </picture>
            </S.FeaturedBlockContainer>
            <MoneyBackGuarantee />
            <S.Benefits>
              <S.BenefitsTitle>{t('purchase2.benefits.title')}</S.BenefitsTitle>
              <ul>
                {USER_BENEFITS.map((item) => (
                  <S.ListItem key={item.id}>
                    <SvgImage svg={checkIcon} width={30} />
                    {t(item.value as any)}
                  </S.ListItem>
                ))}
              </ul>
            </S.Benefits>
            <S.BenefitsTitle>{t('purchase2.benefits.title')}</S.BenefitsTitle>
            <CustomerReviewsListV2 />
            <S.SecurityInfo />
            <S.Button type="button" onClick={handleShowPayment}>
              {t('actions.startNow')}
            </S.Button>
            {selectedSubscription && (
              <S.Disclaimer>
                <Trans
                  i18nKey={
                    PRICE_DESCRIPTION_KEYS[selectedSubscription.product] as any
                  }
                  values={{
                    currency: CURRENCY_SYMBOLS[selectedSubscription.currency],
                    price: selectedSubscription.fullPrice,
                  }}
                />
                <TermsOfUseLink /> {t('purchase.subscription.contactUs')}
                <a href="mailto:support@harmonybit.com">
                  support@harmonybit.com
                </a>
              </S.Disclaimer>
            )}
          </S.Container>
        </S.Wrapper>
      )}
      {isCheckoutShown && <Checkout setIsCheckoutShown={setIsCheckoutShown} />}
    </>
  )
}
